import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart3, TrendingUp, MousePointerClick, Eye, CheckCircle2, Instagram, Mail, Smartphone } from 'lucide-react';
export function Homepage() {
  return <div className="min-h-screen bg-brand-offwhite font-sans selection:bg-brand-sage selection:text-brand-forest" data-id="element-689">
      {/* Floating Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-brand-light z-50" data-id="element-690">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-691">
          <Link to="/" className="flex items-center gap-2" data-id="element-692">
            <img src="https://cdn.magicpatterns.com/uploads/tg3VtfRMGFLgjFkX7oevaV/eaves_favicon_transparent.png" alt="Eaves Pro Logo" className="w-8 h-8" data-id="element-693" />
            <span className="font-serif font-semibold text-xl text-brand-dark" data-id="element-694">
              Eaves Pro
            </span>
          </Link>
          <div className="flex items-center gap-6" data-id="element-695">
            <Link to="/login" className="text-sm font-medium text-brand-dark hover:text-brand-forest transition-colors" data-id="element-696">
              Log in
            </Link>
            <Link to="/login" className="bg-brand-forest text-white text-sm font-medium rounded-full px-5 py-2 hover:bg-brand-forest/90 transition-colors" data-id="element-697">
              Sign up free
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-16" data-id="element-698">
        {/* Hero Section */}
        <section className="py-24 md:py-32 px-6 max-w-6xl mx-auto overflow-hidden" data-id="element-699">
          <div className="flex flex-col md:flex-row items-center gap-16" data-id="element-700">
            <div className="flex-1 text-center md:text-left z-10" data-id="element-701">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} className="inline-block bg-brand-sage text-brand-forest text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-6" data-id="element-702">
                Free for every agent
              </motion.div>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-5xl md:text-6xl font-serif font-bold text-brand-dark leading-tight" data-id="element-703">
                Your work speaks. <br className="hidden md:block" data-id="element-704" />
                <span className="text-brand-forest" data-id="element-705">Now it has a stage.</span>
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-xl text-brand-muted mt-6 max-w-lg mx-auto md:mx-0" data-id="element-706">
                Your Eaves Pro profile is already built — from your
                transactions, your reviews, your neighborhood. Claim it in 60
                seconds.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="mt-10 flex flex-col sm:flex-row items-center justify-center md:justify-start max-w-md mx-auto md:mx-0 shadow-premium rounded-xl" data-id="element-707">
                <div className="flex w-full sm:w-auto" data-id="element-708">
                  <div className="bg-white border border-brand-light rounded-t-xl sm:rounded-l-xl sm:rounded-tr-none px-4 py-3.5 font-mono text-sm text-brand-muted flex items-center border-b-0 sm:border-b sm:border-r-0" data-id="element-709">
                    eaves.pro/
                  </div>
                  <input type="text" placeholder="your-name" className="flex-1 bg-white border-y border-r border-brand-light px-2 py-3.5 font-mono text-sm text-brand-dark focus:outline-none focus:border-brand-forest rounded-tr-xl sm:rounded-none border-b-0 sm:border-b sm:border-r-0" data-id="element-710" />
                </div>
                <Link to="/admin" className="w-full sm:w-auto bg-brand-forest text-white rounded-b-xl sm:rounded-r-xl sm:rounded-bl-none px-6 py-3.5 font-medium hover:bg-brand-forest/90 transition-colors text-center whitespace-nowrap" data-id="element-711">
                  Claim your profile
                </Link>
              </motion.div>
            </div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex-1 relative w-full max-w-sm mx-auto" data-id="element-712">
              <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              repeat: Infinity,
              duration: 5,
              ease: 'easeInOut'
            }} className="relative z-10 transform rotate-2 shadow-2xl rounded-[2.5rem] bg-white border-[8px] border-brand-dark overflow-hidden aspect-[9/19]" data-id="element-713">
                {/* Mini Profile Mockup */}
                <div className="h-32 bg-gradient-to-b from-brand-sage to-brand-sandstoneBg" data-id="element-714"></div>
                <div className="px-6 pb-6 -mt-12 relative bg-white rounded-t-3xl" data-id="element-715">
                  <div className="w-24 h-24 rounded-full border-4 border-white overflow-hidden bg-brand-light mx-auto shadow-sm" data-id="element-716">
                    <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop" alt="Marcus Chen" className="w-full h-full object-cover" data-id="element-717" />
                  </div>
                  <div className="text-center mt-3" data-id="element-718">
                    <h3 className="font-serif font-bold text-xl text-brand-dark" data-id="element-719">
                      Marcus Chen
                    </h3>
                    <p className="text-xs text-brand-muted mt-1" data-id="element-720">
                      Principal Agent · The Chen Group
                    </p>
                  </div>

                  <div className="mt-6 space-y-3" data-id="element-721">
                    <div className="h-12 bg-brand-offwhite rounded-xl border border-brand-light flex items-center px-4" data-id="element-722">
                      <div className="w-6 h-6 rounded-full bg-brand-sage" data-id="element-723"></div>
                      <div className="ml-3 h-3 w-24 bg-brand-light rounded" data-id="element-724"></div>
                    </div>
                    <div className="h-12 bg-brand-offwhite rounded-xl border border-brand-light flex items-center px-4" data-id="element-725">
                      <div className="w-6 h-6 rounded-full bg-brand-sage" data-id="element-726"></div>
                      <div className="ml-3 h-3 w-32 bg-brand-light rounded" data-id="element-727"></div>
                    </div>
                    <div className="h-12 bg-brand-offwhite rounded-xl border border-brand-light flex items-center px-4" data-id="element-728">
                      <div className="w-6 h-6 rounded-full bg-brand-sage" data-id="element-729"></div>
                      <div className="ml-3 h-3 w-20 bg-brand-light rounded" data-id="element-730"></div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Decorative elements behind phone */}
              <div className="absolute top-1/4 -right-12 w-32 h-32 bg-brand-sage rounded-full blur-3xl opacity-60 -z-10" data-id="element-731"></div>
              <div className="absolute bottom-1/4 -left-12 w-40 h-40 bg-brand-sandstone rounded-full blur-3xl opacity-40 -z-10" data-id="element-732"></div>
            </motion.div>
          </div>
        </section>

        {/* Section 2: 'Your profile, already built' */}
        <section className="bg-white py-24 px-6 overflow-hidden" data-id="element-733">
          <div className="max-w-6xl mx-auto flex flex-col-reverse md:flex-row items-center gap-16" data-id="element-734">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1 w-full" data-id="element-735">
              <div className="bg-brand-offwhite p-8 rounded-3xl border border-brand-light shadow-sm relative" data-id="element-736">
                <h4 className="font-serif text-xl text-brand-dark mb-6" data-id="element-737">
                  Your verified record
                </h4>
                <div className="space-y-4" data-id="element-738">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-brand-light flex items-center justify-between" data-id="element-739">
                    <div className="flex items-center gap-3" data-id="element-740">
                      <div className="w-10 h-10 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest" data-id="element-741">
                        <TrendingUp size={20} data-id="element-742" />
                      </div>
                      <div data-id="element-743">
                        <p className="text-sm font-medium text-brand-dark" data-id="element-744">
                          Sale price vs market
                        </p>
                        <p className="text-xs text-brand-muted" data-id="element-745">
                          Top 6% in 94109
                        </p>
                      </div>
                    </div>
                    <span className="font-mono font-medium text-brand-forest" data-id="element-746">
                      +5.2%
                    </span>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-brand-light flex items-center justify-between" data-id="element-747">
                    <div className="flex items-center gap-3" data-id="element-748">
                      <div className="w-10 h-10 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest" data-id="element-749">
                        <CheckCircle2 size={20} data-id="element-750" />
                      </div>
                      <div data-id="element-751">
                        <p className="text-sm font-medium text-brand-dark" data-id="element-752">
                          Verified Transactions
                        </p>
                        <p className="text-xs text-brand-muted" data-id="element-753">
                          Last 24 months
                        </p>
                      </div>
                    </div>
                    <span className="font-mono font-medium text-brand-forest" data-id="element-754">
                      347
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1" data-id="element-755">
              <span className="text-xs uppercase tracking-widest text-brand-forest font-medium" data-id="element-756">
                Claim your profile
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-brand-dark mt-4 leading-tight" data-id="element-757">
                See what your work looks like when someone's paying attention.
              </h2>
              <p className="text-lg text-brand-muted mt-6 leading-relaxed" data-id="element-758">
                Your Eaves Pro profile is already built — from your
                transactions, your reviews, your neighborhood. Claim it in 60
                seconds, then customize it to make it yours.
              </p>
              <Link to="/admin" className="inline-flex items-center gap-2 text-brand-forest font-medium mt-8 hover:text-brand-dark transition-colors group" data-id="element-759">
                Get started for free{' '}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-760" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Section 3: 'One link for everything' */}
        <section className="bg-brand-sage/30 py-24 px-6 overflow-hidden" data-id="element-761">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-16" data-id="element-762">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1" data-id="element-763">
              <span className="text-xs uppercase tracking-widest text-brand-forest font-medium" data-id="element-764">
                One Link
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-brand-dark mt-4 leading-tight" data-id="element-765">
                One link for everything sellers need to trust you.
              </h2>
              <p className="text-lg text-brand-muted mt-6 leading-relaxed" data-id="element-766">
                Your performance. Your reviews from every platform. Your
                listings, videos, and intake forms — all in one place, all on a
                link you own.
              </p>
              <Link to="/admin" className="inline-flex items-center gap-2 text-brand-forest font-medium mt-8 hover:text-brand-dark transition-colors group" data-id="element-767">
                Get started for free{' '}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-768" />
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1 relative w-full" data-id="element-769">
              <div className="relative h-[400px] w-full max-w-md mx-auto" data-id="element-770">
                {/* Stacked Cards Mockup */}
                <motion.div initial={{
                y: 20,
                opacity: 0
              }} whileInView={{
                y: 0,
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.2
              }} className="absolute top-10 left-4 right-12 bg-white p-4 rounded-2xl shadow-lg border border-brand-light z-30 flex items-center gap-4" data-id="element-771">
                  <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest shrink-0" data-id="element-772">
                    <span className="font-serif font-bold" data-id="element-773">Z</span>
                  </div>
                  <div data-id="element-774">
                    <p className="font-medium text-brand-dark" data-id="element-775">
                      Zillow Reviews
                    </p>
                    <p className="text-sm text-brand-muted" data-id="element-776">
                      4.9 ★ (128 reviews)
                    </p>
                  </div>
                </motion.div>

                <motion.div initial={{
                y: 20,
                opacity: 0
              }} whileInView={{
                y: 0,
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.4
              }} className="absolute top-32 left-12 right-4 bg-white p-4 rounded-2xl shadow-lg border border-brand-light z-20 flex items-center gap-4" data-id="element-777">
                  <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest shrink-0" data-id="element-778">
                    <span className="font-serif font-bold" data-id="element-779">G</span>
                  </div>
                  <div data-id="element-780">
                    <p className="font-medium text-brand-dark" data-id="element-781">
                      Google Reviews
                    </p>
                    <p className="text-sm text-brand-muted" data-id="element-782">
                      4.8 ★ (84 reviews)
                    </p>
                  </div>
                </motion.div>

                <motion.div initial={{
                y: 20,
                opacity: 0
              }} whileInView={{
                y: 0,
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.6
              }} className="absolute top-56 left-8 right-8 bg-white p-4 rounded-2xl shadow-lg border border-brand-light z-10 flex items-center gap-4" data-id="element-783">
                  <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest shrink-0" data-id="element-784">
                    <span className="font-serif font-bold" data-id="element-785">S</span>
                  </div>
                  <div data-id="element-786">
                    <p className="font-medium text-brand-dark" data-id="element-787">
                      Selling a home
                    </p>
                    <p className="text-sm text-brand-muted" data-id="element-788">
                      Book a consultation
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section 4: 'Share it everywhere' */}
        <section className="bg-white py-24 px-6 overflow-hidden" data-id="element-789">
          <div className="max-w-6xl mx-auto flex flex-col-reverse md:flex-row items-center gap-16" data-id="element-790">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1 w-full grid grid-cols-2 gap-4" data-id="element-791">
              <div className="space-y-4 mt-8" data-id="element-792">
                <div className="bg-brand-offwhite p-6 rounded-2xl border border-brand-light shadow-sm flex flex-col items-center text-center" data-id="element-793">
                  <Instagram className="text-brand-muted mb-3" size={24} data-id="element-794" />
                  <div className="w-16 h-16 rounded-full bg-brand-light mb-3" data-id="element-795"></div>
                  <div className="h-2 w-20 bg-brand-light rounded mb-4" data-id="element-796"></div>
                  <div className="text-xs font-mono text-brand-forest bg-brand-sage px-2 py-1 rounded w-full truncate" data-id="element-797">
                    eaves.pro/marcuschen
                  </div>
                </div>
                <div className="bg-brand-dark p-6 rounded-2xl shadow-sm flex flex-col justify-between h-32" data-id="element-798">
                  <div className="flex justify-between items-start" data-id="element-799">
                    <div className="w-8 h-8 rounded bg-white/10" data-id="element-800"></div>
                    <Smartphone className="text-white/50" size={20} data-id="element-801" />
                  </div>
                  <div className="text-xs font-mono text-brand-sage truncate" data-id="element-802">
                    eaves.pro/marcuschen
                  </div>
                </div>
              </div>
              <div className="space-y-4" data-id="element-803">
                <div className="bg-white p-6 rounded-2xl border border-brand-light shadow-premium flex flex-col justify-between h-40" data-id="element-804">
                  <div data-id="element-805">
                    <div className="h-3 w-24 bg-brand-dark rounded mb-2" data-id="element-806"></div>
                    <div className="h-2 w-32 bg-brand-muted rounded" data-id="element-807"></div>
                  </div>
                  <div className="text-xs font-mono text-brand-forest bg-brand-sage/50 px-2 py-1 rounded inline-block w-max" data-id="element-808">
                    eaves.pro/marcuschen
                  </div>
                </div>
                <div className="bg-brand-offwhite p-6 rounded-2xl border border-brand-light shadow-sm flex flex-col items-center justify-center h-48" data-id="element-809">
                  <Mail className="text-brand-muted mb-3" size={24} data-id="element-810" />
                  <div className="h-2 w-full bg-brand-light rounded mb-2" data-id="element-811"></div>
                  <div className="h-2 w-3/4 bg-brand-light rounded mb-6" data-id="element-812"></div>
                  <div className="text-xs font-mono text-brand-forest" data-id="element-813">
                    eaves.pro/marcuschen
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1" data-id="element-814">
              <span className="text-xs uppercase tracking-widest text-brand-forest font-medium" data-id="element-815">
                Share everywhere
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-brand-dark mt-4 leading-tight" data-id="element-816">
                One link for social, open houses, and listing presentations.
              </h2>
              <p className="text-lg text-brand-muted mt-6 leading-relaxed" data-id="element-817">
                Share your Eaves Pro profile wherever you show up — Instagram
                bio, business cards, email signatures, QR codes at open houses.
                You own the link. We keep it current automatically.
              </p>
              <div className="mt-6" data-id="element-818">
                <span className="font-mono text-brand-forest bg-brand-sage px-3 py-1.5 rounded-lg inline-block font-medium" data-id="element-819">
                  eaves.pro/your-name
                </span>
              </div>
              <Link to="/admin" className="inline-flex items-center gap-2 text-brand-forest font-medium mt-8 hover:text-brand-dark transition-colors group" data-id="element-820">
                Get started for free{' '}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-821" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Section 5: 'See what's working' */}
        <section className="bg-brand-dark text-white py-24 px-6 overflow-hidden" data-id="element-822">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-16" data-id="element-823">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1" data-id="element-824">
              <span className="text-xs uppercase tracking-widest text-brand-sage font-medium" data-id="element-825">
                Insights
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-brand-offwhite mt-4 leading-tight" data-id="element-826">
                Know who's looking and what they click.
              </h2>
              <p className="text-lg text-gray-400 mt-6 leading-relaxed" data-id="element-827">
                Track profile views, link clicks, and which content drives the
                most engagement. Simple analytics, no complexity.
              </p>
              <Link to="/admin" className="inline-flex items-center gap-2 text-brand-sage font-medium mt-8 hover:text-white transition-colors group" data-id="element-828">
                Get started for free{' '}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-829" />
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1 w-full" data-id="element-830">
              <div className="bg-[#1a2520] p-6 rounded-3xl border border-white/10 shadow-2xl" data-id="element-831">
                <div className="flex items-center justify-between mb-8" data-id="element-832">
                  <h3 className="font-serif text-lg text-white" data-id="element-833">Performance</h3>
                  <div className="text-xs text-gray-400 bg-white/5 px-3 py-1 rounded-full" data-id="element-834">
                    Last 7 days
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-835">
                  <div className="bg-white/5 p-4 rounded-xl border border-white/5" data-id="element-836">
                    <div className="flex items-center gap-2 text-gray-400 mb-2" data-id="element-837">
                      <Eye size={16} data-id="element-838" />
                      <span className="text-sm" data-id="element-839">Views</span>
                    </div>
                    <div className="text-3xl font-mono text-white" data-id="element-840">247</div>
                    <div className="text-xs text-brand-sage mt-1" data-id="element-841">
                      ↑ 12% vs last week
                    </div>
                  </div>
                  <div className="bg-white/5 p-4 rounded-xl border border-white/5" data-id="element-842">
                    <div className="flex items-center gap-2 text-gray-400 mb-2" data-id="element-843">
                      <MousePointerClick size={16} data-id="element-844" />
                      <span className="text-sm" data-id="element-845">Clicks</span>
                    </div>
                    <div className="text-3xl font-mono text-white" data-id="element-846">83</div>
                    <div className="text-xs text-brand-sage mt-1" data-id="element-847">
                      ↑ 8% vs last week
                    </div>
                  </div>
                </div>

                {/* Decorative Bar Chart */}
                <div className="h-40 flex items-end justify-between gap-2 px-2" data-id="element-848">
                  {[40, 65, 45, 80, 55, 90, 70].map((height, i) => <div key={i} className="w-full flex gap-1 items-end justify-center group" data-id="element-849">
                      <div className="w-1/2 bg-brand-forest/50 rounded-t-sm group-hover:bg-brand-forest transition-colors" style={{
                    height: `${height}%`
                  }} data-id="element-850"></div>
                      <div className="w-1/2 bg-brand-sage/50 rounded-t-sm group-hover:bg-brand-sage transition-colors" style={{
                    height: `${height * 0.4}%`
                  }} data-id="element-851"></div>
                    </div>)}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="bg-brand-forest py-24 px-6 text-center" data-id="element-852">
          <div className="max-w-3xl mx-auto" data-id="element-853">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-3xl md:text-4xl font-serif font-semibold text-white" data-id="element-854">
              Your profile is waiting.
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-xl text-brand-sage mt-4 mb-10" data-id="element-855">
              Claim your free Eaves Pro profile in 60 seconds.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="flex flex-col sm:flex-row items-center justify-center max-w-md mx-auto shadow-2xl rounded-xl" data-id="element-856">
              <div className="flex w-full sm:w-auto" data-id="element-857">
                <div className="bg-white border border-white rounded-t-xl sm:rounded-l-xl sm:rounded-tr-none px-4 py-3.5 font-mono text-sm text-brand-muted flex items-center border-b-0 sm:border-b sm:border-r-0" data-id="element-858">
                  eaves.pro/
                </div>
                <input type="text" placeholder="your-name" className="flex-1 bg-white border-y border-r border-white px-2 py-3.5 font-mono text-sm text-brand-dark focus:outline-none rounded-tr-xl sm:rounded-none border-b-0 sm:border-b sm:border-r-0" data-id="element-859" />
              </div>
              <Link to="/admin" className="w-full sm:w-auto bg-brand-dark text-white rounded-b-xl sm:rounded-r-xl sm:rounded-bl-none px-6 py-3.5 font-medium hover:bg-black transition-colors text-center whitespace-nowrap" data-id="element-860">
                Claim your profile
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-brand-dark text-gray-400 py-12 px-6 border-t border-white/10" data-id="element-861">
        <div className="max-w-6xl mx-auto flex flex-col items-center" data-id="element-862">
          <Link to="/" className="flex items-center gap-2 mb-8 opacity-80 hover:opacity-100 transition-opacity" data-id="element-863">
            <img src="https://cdn.magicpatterns.com/uploads/tg3VtfRMGFLgjFkX7oevaV/eaves_favicon_transparent.png" alt="Eaves Pro Logo" className="w-6 h-6 grayscale" data-id="element-864" />
            <span className="font-serif font-semibold text-lg text-white" data-id="element-865">
              Eaves Pro
            </span>
          </Link>

          <div className="flex gap-6 mb-8 text-sm" data-id="element-866">
            <a href="#" className="hover:text-white transition-colors" data-id="element-867">
              About
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-868">
              Privacy
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-869">
              Terms
            </a>
          </div>

          <div className="text-xs text-center" data-id="element-870">
            © 2026 Eaves Technologies LLC · witheaves.com
          </div>
        </div>
      </footer>
    </div>;
}